import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { OnboardingPageContainer } from './OnboardingPageContainer';
import { SignUpPage, SignUpFormData } from './SignUpPage';

type ContainerView = 'signup' | 'onboarding';

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;

export const SignUpPageContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activationCode = searchParams.get('activation');
  const encodedEmail = searchParams.get('ie');

  // The signup prompt modal collects the first-step data, then routes here so the
  // visitor lands in onboarding instead of re-entering everything.
  const forwardedSignUpData = (location.state as { signUpData?: SignUpFormData } | null)
    ?.signUpData;

  // Decode base64 encoded email from family invite link
  const inviteEmail = useMemo(() => {
    if (!encodedEmail) return null;

    try {
      const decoded = atob(encodedEmail);

      if (!EMAIL_REGEX.test(decoded)) return null;

      return decoded;
    } catch {
      return null;
    }
  }, [encodedEmail]);
  const isFamilyInvite = inviteEmail !== null;

  const { user } = useAppSelector((state) => state.user);

  const [currentView, setCurrentView] = useState<ContainerView>(
    forwardedSignUpData ? 'onboarding' : 'signup',
  );
  const [signUpData, setSignUpData] = useState<SignUpFormData | null>(forwardedSignUpData ?? null);

  useEffect(() => {
    const accessToken = getBackrsToken();

    // If user is already authenticated, redirect to home
    if (user && accessToken && currentView === 'signup') {
      navigate(APP_ROUTE.HOME);
    }
  }, [user, navigate, currentView]);

  const handleSignUpComplete = (data: SignUpFormData) => {
    setSignUpData(data);
    setCurrentView('onboarding');
  };

  if (currentView === 'onboarding' && signUpData) {
    return <OnboardingPageContainer signUpData={signUpData} activationCode={activationCode} />;
  }

  return (
    <SignUpPage
      onSignUpComplete={handleSignUpComplete}
      initialEmail={inviteEmail}
      isFamilyInvite={isFamilyInvite}
    />
  );
};
